import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { computed, ref } from 'vue';
import axios from 'axios';
import { backend_url } from '@/config/config';

export const useAuthStore = defineStore('auth', () => {
  const token = useStorage<string>('fd_token', '');
  const refreshToken = useStorage<string>('fd_refreshToken', '');
  const email = useStorage<string>('fd_email', '');
  const name = useStorage<string>('fd_name', '');
  const firstname = useStorage<string>('fd_firstname', '');
  const nickname = useStorage<string>('fd_nickname', '');
  const profile_picture = useStorage<string>('fd_profile_picture', '');
  const role = useStorage<string>('fd_role', '');
  // Rôles mémorisés : ne comptent qu'avec un jeton valide (cf. `scopes` plus bas), sinon un
  // jeton expiré laissait l'interface admin visible à un visiteur affiché comme déconnecté.
  const storedScopes = useStorage<string[]>('fd_scopes', [], localStorage, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : []),
      write: (v) => JSON.stringify(v),
    },
  });
  const userId = useStorage<number | null>('fd_userId', null, localStorage, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v),
    },
  });

  const isTokenValid = (t: string): boolean => {
    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      return Date.now() < payload.exp * 1000;
    } catch {
      return false;
    }
  };

  const isAuthenticated = computed(() => !!token.value && isTokenValid(token.value));
  const scopes = computed(() => (isAuthenticated.value ? storedScopes.value : []));
  const isAdmin = computed(() => scopes.value.includes('super_admin'));
  const isSuperAdmin = computed(() => scopes.value.includes('super_admin'));

  const profilePictureUrl = computed(() =>
    profile_picture.value ? `${backend_url}${profile_picture.value}` : ''
  );

  const setAuthData = (data: {
    token: string;
    refreshToken?: string;
    email: string;
    name: string;
    firstname: string;
    nickname: string;
    profile_picture: string;
    role: string;
    scopes: string[];
    userId?: number;
  }) => {
    token.value = data.token;
    if (data.refreshToken) refreshToken.value = data.refreshToken;
    email.value = data.email;
    name.value = data.name;
    firstname.value = data.firstname;
    nickname.value = data.nickname;
    profile_picture.value = data.profile_picture || '';
    role.value = data.role;
    storedScopes.value = data.scopes || [];
    if (data.userId) userId.value = data.userId;
  };

  const logout = () => {
    localStorage.clear();
    token.value = '';
    refreshToken.value = '';
    email.value = '';
    name.value = '';
    firstname.value = '';
    nickname.value = '';
    profile_picture.value = '';
    role.value = '';
    storedScopes.value = [];
    userId.value = null;
  };

  const refreshAccessToken = async (): Promise<boolean> => {
    if (!refreshToken.value) {
      logout();
      return false;
    }
    try {
      const response = await axios.post(
        `${backend_url}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${refreshToken.value}` } }
      );
      if (response.data.access_token) {
        token.value = response.data.access_token;
        if (response.data.refresh_token) refreshToken.value = response.data.refresh_token;
        return true;
      }
      throw new Error('No access token in response');
    } catch {
      logout();
      return false;
    }
  };

  const purgeIfInvalid = () => {
    if (!isAuthenticated.value && (token.value || name.value || storedScopes.value.length)) {
      logout();
    }
  };

  return {
    token, refreshToken, email, name, firstname, nickname,
    profile_picture, profilePictureUrl, role, scopes, userId,
    isAuthenticated, isAdmin, isSuperAdmin,
    setAuthData, logout, refreshAccessToken, isTokenValid, purgeIfInvalid,
  };
});
